import React, { useState, useRef } from 'react';
import { Modal, Input, InputNumber, message, Button, Space, Form } from 'antd';
import { SoundOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import { audioLibraryApi } from '../../services/audioLibraryApi';

const { TextArea } = Input;

interface GeneratedSfxData {
  url: string;
  name: string;
  description: string;
  type: 'sound_effect';
  duration: number;
  fileSize: number;
  format: string;
}

interface GenerateSfxModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const GenerateSfxModal: React.FC<GenerateSfxModalProps> = ({
  visible,
  onClose,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatedSfx, setGeneratedSfx] = useState<GeneratedSfxData | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handleGenerate = async () => {
    try {
      const values = await form.validateFields();
      setGenerating(true);

      const result = await audioLibraryApi.generateSoundEffect({
        prompt: values.prompt,
        durationSeconds: values.durationSeconds || 10,
        name: values.name || undefined,
      });

      const sfx = result.data || result;
      setGeneratedSfx({
        url: sfx.url,
        name: sfx.name,
        description: sfx.description,
        type: 'sound_effect',
        duration: sfx.duration,
        fileSize: sfx.fileSize,
        format: sfx.format,
      });
      message.success('Sound effect generated! Listen to preview before saving.');
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
      message.error('Failed to generate sound effect: ' + errorMsg);
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!generatedSfx) return;
    const name = form.getFieldValue('name');
    if (!name?.trim()) {
      message.warning('Please enter a name before saving');
      return;
    }
    try {
      setSaving(true);
      await audioLibraryApi.saveGeneratedAudio({
        url: generatedSfx.url,
        name: name.trim(),
        description: generatedSfx.description,
        type: 'sound_effect',
        duration: generatedSfx.duration,
        fileSize: generatedSfx.fileSize,
        format: generatedSfx.format,
        bpm: 0,
        mood: [],
        instruments: [],
        genre: 'sfx',
        layering: '',
      });
      message.success('Sound effect saved to library!');
      handleReset();
      onSuccess();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
      message.error('Failed to save sound effect: ' + errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setGeneratedSfx(null);
    handleGenerate();
  };

  const handleReset = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    form.resetFields();
    setGeneratedSfx(null);
  };

  const handleCancel = () => {
    handleReset();
    onClose();
  };

  return (
    <Modal
      title="Generate Sound Effect"
      open={visible}
      onCancel={handleCancel}
      width={550}
      footer={
        generatedSfx ? (
          <Space>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              icon={<ReloadOutlined />}
              loading={generating}
              disabled={saving}
              onClick={handleRegenerate}
            >
              Regenerate
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              disabled={generating}
              onClick={handleSave}
            >
              Save to Library
            </Button>
          </Space>
        ) : (
          <Space>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              type="primary"
              loading={generating}
              onClick={handleGenerate}
            >
              Generate
            </Button>
          </Space>
        )
      }
    >
      {generatedSfx && (
        <div style={{ marginBottom: 16, padding: 16, background: '#fff7e6', borderRadius: 8, border: '1px solid #ffd591' }}>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Space>
              <SoundOutlined style={{ color: '#fa8c16', fontSize: 16 }} />
              <span style={{ fontWeight: 600 }}>Preview: {generatedSfx.name}</span>
            </Space>
            <audio
              ref={audioRef}
              controls
              src={generatedSfx.url}
              style={{ width: '100%' }}
            />
            <span style={{ fontSize: 12, color: '#666' }}>
              {generatedSfx.format?.toUpperCase()} · {Math.round(generatedSfx.duration)}s · {(generatedSfx.fileSize / 1024).toFixed(0)} KB
            </span>
          </Space>
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        initialValues={{ durationSeconds: 10 }}
      >
        <Form.Item
          name="prompt"
          label="Sound Effect Description"
          rules={[{ required: true, message: 'Please describe the sound effect you want' }]}
        >
          <TextArea
            rows={5}
            placeholder="Describe the sound effect in detail, e.g.: 'Thunder rumbling in the distance, followed by a sharp lightning crack and rain hitting a metal roof'"
            disabled={generating}
          />
        </Form.Item>

        <Form.Item
          name="durationSeconds"
          label="Duration (seconds)"
          rules={[{ required: true, message: 'Please enter duration' }]}
          extra="ElevenLabs supports 1-30 seconds"
        >
          <InputNumber min={1} max={30} style={{ width: '100%' }} disabled={generating} />
        </Form.Item>

        <Form.Item
          name="name"
          label="Name"
        >
          <Input placeholder="e.g., Thunder Crack, Footsteps on Gravel" disabled={generating} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default GenerateSfxModal;
